import { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Network, Users, MessageSquare, LogOut, Briefcase, Menu, X, ClipboardCheck, Radio } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import CommandPalette from './ui/CommandPalette';

const BASE_NAV = [
    { icon: LayoutDashboard, label: 'Overview',        path: '/manager/overview' },
    { icon: Network,         label: 'La Mia Rete',     path: '/manager/network' },
    { icon: Users,           label: 'Clienti Rete',   path: '/manager/contracts' },
    { icon: Radio,           label: 'CTE & Prospetto', path: '/manager/cte' },
    { icon: MessageSquare,   label: 'Comunicazioni',   path: '/manager/communications' },
];

const ManagerLayout = () => {
    const { effectiveProfile: profile, signOut, impersonating } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [pendingCensimenti, setPendingCensimenti] = useState(0);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Ctrl+K / Cmd+K apre la Command Palette
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setPaletteOpen(o => !o);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        if (!profile?.id) return;
        supabase
            .from('agent_censimenti')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending')
            .eq('manager_id', profile.id)  // Bug #1: filtra esplicitamente per questo manager
            .then(({ count }) => setPendingCensimenti(count || 0));
    }, [profile?.id]);

    const managerNavItems = [
        ...BASE_NAV,
        { icon: ClipboardCheck, label: 'Censimenti', path: '/manager/censimento', badge: pendingCensimenti || null },
    ];

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const sidebarContent = (
        <>
            {/* Logo */}
            <div className="flex flex-col items-center py-6 px-4 border-b border-white/10">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl mb-3"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9, #0d9488)' }}>
                    <Briefcase className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-bold text-sm tracking-wide">ONET Manager</p>
                <span className="mt-1.5 text-[10px] font-semibold tracking-widest uppercase px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(14,165,233,0.15)', color: '#38bdf8', border: '1px solid rgba(14,165,233,0.3)' }}>
                    Manager
                </span>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {managerNavItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => cn(
                            'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all relative',
                            isActive
                                ? 'bg-sky-500/15 text-sky-300 font-medium'
                                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && (
                                    <motion.div
                                        layoutId="managerActiveIndicator"
                                        className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r-full"
                                        style={{ background: 'linear-gradient(to bottom, #0ea5e9, #0d9488)' }}
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                                    />
                                )}
                                <item.icon className={cn('w-4 h-4 flex-shrink-0', isActive && 'text-sky-400')} />
                                <span className="flex-1">{item.label}</span>
                                {item.badge > 0 && (
                                    <motion.span
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full"
                                        style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}
                                    >
                                        {item.badge}
                                    </motion.span>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Bottom */}
            <div className="p-3 border-t border-white/10 space-y-2">
                {profile && (
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #0ea5e9, #0d9488)' }}>
                            {(profile.name?.[0] || profile.email?.[0])?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-200 truncate">{profile.name || profile.email}</p>
                            <p className="text-[10px] text-sky-400">Manager</p>
                        </div>
                    </div>
                )}
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 border border-red-500/20 transition-all"
                >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                </button>
            </div>
        </>
    );

    return (
        <div className="flex h-screen bg-[#080f12] overflow-hidden">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar desktop */}
            <motion.aside
                initial={{ x: -24, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="hidden lg:flex w-64 flex-shrink-0 flex-col m-3 rounded-2xl border border-white/10 overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #0d1c1f 0%, #08131a 100%)' }}
            >
                {sidebarContent}
            </motion.aside>

            {/* Sidebar mobile drawer */}
            <div className={`
                fixed inset-y-0 left-0 z-50 w-64 flex flex-col
                border-r border-white/10 overflow-hidden
                transition-transform duration-300 ease-in-out lg:hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `} style={{ background: 'linear-gradient(160deg, #0d1c1f 0%, #08131a 100%)' }}>
                <button
                    onClick={() => setSidebarOpen(false)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
                {sidebarContent}
            </div>

            {/* Main */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Mobile top bar */}
                <div className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#080f12]">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-sky-400" />
                        <span className="text-white font-semibold text-sm">Manager Panel</span>
                    </div>
                </div>

                <main className={`flex-1 overflow-y-auto lg:pt-0 pt-14 ${impersonating ? 'lg:pt-10' : ''}`}>
                    <Outlet />
                </main>
            </div>

            {/* Command Palette (Ctrl+K) */}
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
};

export default ManagerLayout;
